<template>
  <div v-bind="$attrs" ref="container" class="relative w-full h-full">
    <canvas v-show="ready" ref="canvas" class="absolute inset-0 w-full h-full"></canvas>
    <nav class="absolute right-4 bottom-4 md:right-6 md:bottom-6 z-10 flex text-white bg-gray-700 rounded-full opacity-40 hover:opacity-90 transition-opacity">
      <button @click="zoomViewport(-1)" class="p-1">
        <Icon type="remove" size="8" />
      </button>
      <button @click="zoomViewport(1)" class="p-1">
        <Icon type="add" size="8" />
      </button>
    </nav>
  </div>
</template>

<script>
export default {
  inheritAttrs: false,
}
</script>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import { Application, Graphics, Assets, Sprite, Text, utils } from 'pixi.js';
import { Viewport } from 'pixi-viewport';
import Icon from 'Web/Shared/Icon.vue';
import useSeatplan from '@/compose/seatplan';
import debounce from 'lodash/debounce';
import each from 'lodash/each';
import clone from '@/helpers/clone';
import echo from '@/helpers/echo';
import Http from '@/helpers/http';

const props = defineProps({
  seatplan: {
    type: Object,
    default: null,
  },
  selected: {
    type: Array,
    default: () => [],
  },
  blocked: {
    type: Array,
    default: () => [],
  },
  refresh: {
    type: String,
    required: true,
  },
  cart: {
    type: String,
    required: true,
  },
  wheel: {
    type: Boolean,
    default: false,
  },
  channel: {
    type: String,
    required: true,
  },
  rules: {
    type: Array,
    default: () => ['preventOrphans', 'consecutive'],
  }
});

const container = ref(null);
const canvas = ref(null);
const ready = ref(null);

const colors = {
  background: 0x111b2d,
  white: 0xffffff,
  black: 0x000000,
  blocked: 0x293242,
  deselect: 0xea5770,
  default: {
    default: 0x2563eb,
    selected: 0x1d4ed8,
  },
  internal: {
    default: 0xf09341,
    selected: 0xda761e,
  },
  pga: {
    default: 0x7c68f1,
    selected: 0x624bf0,
  },
  pgb: {
    default: 0x70cce0,
    selected: 0x00d1d1,
  },
  pgc: {
    default: 0xD196FF,
    selected: 0xBE70FB,
  },
};

const lightColors = [
  ...Object.values(colors.pgb),
  ...Object.values(colors.pgc),
];

const icons = {
  check: null,
  close: null,
  minus: null,
  plus: null,
  sync: null,
  wheelchair: null,
  unavailable: null,
}

const spin = {
  //
};

const {
  selected,
  accepted,
  seats,
  setup,
  start,
  on,
} = useSeatplan(props.cart, props.rules, updateSeat);

let app,
  pointer,
  focus,
  viewport,
  screenWidth,
  screenHeight,
  zoomStep,
  zoomSteps,
  zoomChange,
  minScale,
  maxScale;

const handleResize = debounce(setupViewport, 50);

function renderStage(sprites) {
  each(sprites, item => {
    Assets.load(item.source).then(resource => {
      const sprite = new Sprite(resource);

      sprite.anchor.set(0, 0);
      sprite.width  = item.width;
      sprite.height = item.height;
      sprite.x = item.x;
      sprite.y = item.y;

      viewport.addChildAt(sprite, 0);
    });
  });
}

function renderSeats(seats) {
  each(seats, seat => {
    const graphic = new Graphics();

    graphic.beginFill(seat.defaultColor, 1);
    graphic.drawCircle(seat.x, seat.y, 16);
    graphic.endFill();

    graphic.on('pointerdown', () => {
      pointer = seat;
    });

    graphic.on('pointerup', () => {
      if (seat.id === pointer?.id) {
        seat.onClick();
      }
    });

    graphic.on('pointerupoutside', () => {
      pointer = null;
    });

    graphic.on('pointerover', () => {
      pointer = seat;
      focus = seat;
      seat.onFocus();
    });

    graphic.on('pointerout', () => {
      focus = null;
      seat.onBlur();
    });

    seat.graphic = graphic;

    updateSeat(seat, false);

    viewport.addChild(graphic);
  });
}

function renderSeat(seat, invalidate, options) {
  const {
    events = null,
    border = null,
    borderColor = null,
    borderWidth = null,
    outline = null,
    outlineColor = null,
    outlineWidth = null,
    color = null,
    icon = null,
    spin = null,
    tint = null,
  } = options;

   // Interaction
  seat.graphic.eventMode  = events ? 'static' : 'none';
  seat.graphic.buttonMode = events;

  // Border
  seat.graphic.geometry.graphicsData[0].lineStyle.alignment = 0;
  seat.graphic.geometry.graphicsData[0].lineStyle.visible = true;
  seat.graphic.geometry.graphicsData[0].lineStyle.color = borderColor;
  seat.graphic.geometry.graphicsData[0].lineStyle.width = borderWidth;
  seat.graphic.geometry.graphicsData[0].lineStyle.visible = border;

  // Color
  seat.graphic.geometry.graphicsData[0].fillStyle.color = color;

  // Remove any children
  seat.graphic.removeChildren();

  if (outline) {
    const graphic = new Graphics();

    graphic.beginFill(seat.defaultColor, 0);
    graphic.drawCircle(seat.x, seat.y, 18);
    graphic.endFill();

    graphic.geometry.graphicsData[0].lineStyle.alignment = 1;
    graphic.geometry.graphicsData[0].lineStyle.visible = true;
    graphic.geometry.graphicsData[0].lineStyle.color = outlineColor;
    graphic.geometry.graphicsData[0].lineStyle.width = outlineWidth;
    graphic.geometry.graphicsData[0].lineStyle.visible = outline;

    seat.graphic.addChild(graphic);
  }

  if (icon) {
    const sprite = new Sprite(icons[icon]);

    sprite.anchor.set(0.5);
    sprite.width = 32;
    sprite.height = 32;
    sprite.x = seat.x;
    sprite.y = seat.y;

    if (tint) {
      sprite.tint = tint;
    }

    seat.graphic.addChild(sprite);

    if (spin)
      startSpinning(seat.id, sprite);
    else
      stopSpinning(seat.id);
  } else {
    const text = new Text(seat.model.name?.match(/[0-9]+/), {
      fontSize: 14,
      fill: tint,
    });

    text.anchor.set(0.5);
    text.x = seat.x;
    text.y = seat.y;

    seat.graphic.addChild(text);
  }

  if (invalidate) {
    seat.graphic.geometry.invalidate();
  }
}

function updateSeat(seat, invalidate = true) {
  const colorSelected = colors[seat.model.theme || 'default'].selected;
  const colorDefault = colors[seat.model.theme || 'default'].default;

  let events = true;
  let border = false;
  let borderColor = colors.white;
  let borderWidth = 1;
  let outline = false;
  let outlineColor = colors.white;
  let outlineWidth = 3;
  let color = colorDefault;
  let icon = seat.model.icon;
  let spin = false;
  let tint = colors.white;

  const render = () => {
    if (lightColors.includes(color))
      tint = colors.background;

    if (outline)
      border = false;

    renderSeat(seat, invalidate, {
      events,
      border,
      borderColor,
      borderWidth,
      outline,
      outlineColor,
      outlineWidth,
      color,
      icon,
      spin,
      tint,
    });
  }

  // Blocked
  if (seat.state.blocked) {
    events = false;
    color = colors.blocked;

    if (['unavailable', 'wheelchair'].includes(icon)) {
      color = colors.blocked;
      border = false;

      if (! seat.available) {
        color = colors.background;
        border = true;
      }
    }

    return render();
  }

  if (['unavailable', 'wheelchair'].includes(icon)) {
    color = colors.background;
    border = true;
  }

  // Syncing
  if (seat.state.selected && !seat.state.accepted) {
    outline = true;
    color = colorSelected;
    icon = 'sync';
    spin = true;

    return render();
  }

  // Selected or accepted
  switch (true) {
    case seat.state.selected && seat.state.accepted:
      outline = true;
      color = colorSelected;
      icon = 'check';

      break;

    case seat.state.selected:
      outline = true;
      color = colorSelected;

      break;
  }

  // Deselect
  if (
    (seat.state.anticipate === 'deselect') ||
    (seat.state.selected && seat.state.focus && seat.state.valid)
  ) {
    outline = true;
    color = colors.deselect;
    icon = 'minus';
  }

  // Deselect not allowed
  if (seat.state.selected && seat.state.focus && !seat.state.valid) {
    icon = 'close';
  }

  // Select not allowed
  // if (!seat.state.selected && seat.state.focus && !seat.state.valid) {
  //   icon = 'close';
  // }

  // Select or deselect not allowed
  // if (seat.state.focus && !seat.state.valid) {
  //   icon = 'close';
  // }

  // Select
  if (
    (seat.state.anticipate === 'select') ||
    (!seat.state.selected && seat.state.focus && seat.state.valid)
  ) {
    outline = true;
    color = colorSelected;
    icon = 'plus';
  }

  render();
}

function startSpinning(key, element) {
  spin[key] = element;
}

function stopSpinning(...keys) {
  Object.keys(spin).forEach(key => {
    if (keys.length = 0 || keys.includes(key))
      delete spin[key];
  });
}

function setupViewport() {
  const { width, height } = container.value.getBoundingClientRect();

  if (width === screenWidth && height === screenHeight) return;

  const worldHeight = props.seatplan.display_height;
  const worldWidth = props.seatplan.display_width;

  screenWidth = width;
  screenHeight = height;
  minScale = Math.min(width / worldWidth, height / worldHeight);
  maxScale = 2;

  const range = (screenWidth / maxScale) - (screenWidth / minScale);

  zoomStep   = 0;
  zoomSteps  = Math.ceil(Math.abs(range) / 600);
  zoomChange = range / zoomSteps;

  if (viewport) {
    viewport.clampZoom({ minScale, maxScale });
    viewport.resize(width, height);
    viewport.fit();
    viewport.moveCenter(0, 0);
    return;
  }

  viewport = new Viewport({
    screenWidth,
    screenHeight,
    worldWidth,
    worldHeight,
    events: app.renderer.events,
  });

  app.stage.addChild(viewport);

  viewport.on('drag-start', () => {
    pointer = null;
  });

  viewport.on('pinch-start', () => {
    pointer = null;
  });

  viewport.drag({
    wheel: false,
  });

  viewport.pinch({
    //
  });

  viewport.clampZoom({
    minScale,
    maxScale,
  });

  viewport.clamp({
    direction: 'all',
    underflow: 'center',
  });

  viewport.fit();

  if (props.wheel) {
    viewport.wheel({
      //
    });
  }
}

function zoomViewport(direction) {
  const nextZoomStep = zoomStep + direction;

  if (!viewport || nextZoomStep > zoomSteps || nextZoomStep < 0) return;

  viewport.zoom(direction * zoomChange, 'center');
  zoomStep = nextZoomStep;
}

function blockSeat(key) {
  if (key in seats.value) {
    const seat = seats.value[key];
    seat.state.blocked = true;
    seat.flush();
  }
}

function unblockSeat(key) {
  if (key in seats.value) {
    const seat = seats.value[key];
    seat.state.blocked = false;
    seat.flush();
  }
}

function syncSeats(data) {
  data.forEach(value => {
    value.startsWith('-') ? unblockSeat(value.slice(1)) : blockSeat(value);
  });

  focus && focus.flush();
}

async function refresh() {
  const response = await Http.get(props.refresh);

  selected.value = clone(response.selected);
  accepted.value = clone(response.selected);

  syncSeats(response.blocked)
}

on('error', () => {
  pointer && pointer.flush();
});

onMounted(async () => {
  app = new Application({
    backgroundColor: colors.background,
    resolution: window.devicePixelRatio || 1,
    antialias: true,
    resizeTo: container.value,
    view: canvas.value,

    autoDensity: true,
    resolution: 2,
  });

  setupViewport();

  setup(props.seatplan);

  // https://www.leshylabs.com/apps/sstool/
  // JSON-TP-Hash
  await Assets.load('/media/seatplan/sprites.json?v=' + Date.now()).then(resource => {
    Object.keys(icons).forEach(key => {
      if (key in resource.textures) {
        icons[key] = resource.textures[key];
      }
    });
  });

  selected.value = clone(props.selected);
  accepted.value = clone(props.selected);

  syncSeats(props.blocked);

  echo.join(props.channel).listen('EventSynced', syncSeats);

  viewport.removeChildren();

  renderStage(props.seatplan.props);
  renderSeats(seats.value);

  await refresh();

  start();

  ready.value = true;

  let rotation = 0;

  app.ticker.add(delta => {
    each(spin, element => element.rotation = rotation);
    rotation += 0.1 * delta;
  });

  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  echo.leave(props.channel);
  window.removeEventListener('resize', handleResize);
  utils.clearTextureCache();
  app.destroy();
});

</script>
